import React from 'react';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Resume from './pages/Resume';
import HireMe from './pages/HireMe';
import LinkedIn from './pages/LinkedIn';
import GitHub from './pages/GitHub';
import Abra from './pages/Abra';
import Twitter from './pages/Twitter';
import ProjectsDetail from './pages/ProjectsDetail';
import ProjectDetail from './pages/ProjectDetail';
import WorkDetail from './pages/WorkDetail';
import BlogDetail from './pages/BlogDetail';
import ExperienceDetail from './pages/ExperienceDetail';
import EducationDetail from './pages/EducationDetail';
import LabsIndex from './pages/LabsIndex';
import LabProjectDetail from './pages/LabProjectDetail';
import DemoCapture from './pages/DemoCapture';
import NotFound from './pages/NotFound';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { blogPosts } from './shared/blogPosts';

const KNOWN_ROUTES = [
  '/',
  '/resume',
  '/hireme',
  '/Hireme',
  '/linkedin',
  '/github',
  '/abra',
  '/twitter',
  '/x',
  '/projects',
  '/labs',
  '/work',
  '/404',
];

// Posts live at the site root, so their slugs share a namespace with every page
// route. A collision would be shadowed silently by the page (static routes rank
// above dynamic ones), so fail loudly in development instead.
const RESERVED_SLUGS = new Set(
  KNOWN_ROUTES.map((route) => route.replace(/^\//, '')).concat([
    'reads',
    'blog',
    'experience',
    'education',
  ])
);

if (process.env.NODE_ENV !== 'production') {
  const collisions = blogPosts
    .map((post) => post.slug)
    .filter((slug) => RESERVED_SLUGS.has(slug));
  if (collisions.length) {
    throw new Error(
      `Blog slug would be shadowed by a page route: ${collisions.join(', ')}`
    );
  }
}

// Posts used to live under /reads/. Those URLs stay alive permanently.
const ReadsRedirect: React.FC = () => {
  const { blogSlug } = useParams<{ blogSlug: string }>();
  return <Navigate to={`/${blogSlug ?? ''}`} replace />;
};

function App() {
  const location = useLocation();
  const isPost = blogPosts.some(
    (post) => location.pathname === `/${post.slug}`
  );
  const isKnownEditorial =
    location.pathname === '/' ||
    location.pathname === '/work' ||
    location.pathname === '/404' ||
    isPost ||
    location.pathname.startsWith('/reads/');
  // Anything we do not route at all also gets the quiet chrome-free treatment,
  // so a 404 does not render a navbar pointing at a dead page.
  const isUnknown =
    !KNOWN_ROUTES.includes(location.pathname) &&
    !/^\/(reads|projects|labs|experience|education)\//.test(location.pathname);
  const isEditorialPage = isKnownEditorial || isUnknown;

  return (
    <div className={`${isEditorialPage ? 'home-app' : 'bg-minimal-black'} min-h-screen`}>
        {!isEditorialPage && <Navbar />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/hireme" element={<HireMe />} />
          <Route path="/Hireme" element={<HireMe />} />
          <Route path="/linkedin" element={<LinkedIn />} />
          <Route path="/github" element={<GitHub />} />
          <Route path="/abra" element={<Abra />} />
          <Route path="/twitter" element={<Twitter />} />
          <Route path="/x" element={<Twitter />} />
        <Route path="/projects" element={<ProjectsDetail />} />
        <Route path="/projects/:projectSlug" element={<ProjectDetail />} />
        <Route path="/labs" element={<LabsIndex />} />
        <Route path="/labs/:projectSlug" element={<LabProjectDetail />} />
        <Route path="/work" element={<WorkDetail />} />
        <Route path="/experience/:experienceSlug" element={<ExperienceDetail />} />
        <Route path="/education/:educationSlug" element={<EducationDetail />} />
        <Route path="/stateless/demo" element={<DemoCapture />} />
        <Route
          path="/reads/stateless-desktops/demo"
          element={<Navigate to="/stateless/demo" replace />}
        />
        <Route
          path="/reads/stateless/demo"
          element={<Navigate to="/stateless/demo" replace />}
        />
        {/* Old post URLs, kept so nothing already shared can die. */}
        <Route path="/reads/:blogSlug" element={<ReadsRedirect />} />
        <Route path="/reads" element={<Navigate to="/work" replace />} />
        <Route path="/blog" element={<Navigate to="/work" replace />} />
        <Route path="/404" element={<NotFound />} />
        {/* Posts sit at the root. Static routes above rank higher, so this only
            catches slugs; unknown ones fall through to the 404 inside. */}
        <Route path="/:blogSlug" element={<BlogDetail />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
    </div>
  );
}

export default App;
